import React, { useContext, useState, useEffect } from 'react';
import ThemeContext from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Sidebar from './Sidebar';
import appIcon from '../../assets/app-icon.png';

const Layout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { theme } = useContext(ThemeContext) || {};
  const { credits, userProfile } = useAuth();
  const isDark = theme === 'dark';

  const isMuzoPlus =
    (userProfile?.plan?.status === 'active' && userProfile?.plan?.type === 'pro') ||
    userProfile?.saas_usage?.subscription_active;

  // Detect mobile breakpoint
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <div
      className={`flex min-h-screen transition-colors duration-300 ${
        isDark ? 'bg-slate-950 text-slate-100' : 'bg-slate-50 text-slate-900'
      }`}
    >
      {/* Mobile Header - Fixed top bar */}
      {isMobile && (
        <header
          className={`fixed top-0 left-0 right-0 z-50 h-14 flex items-center justify-between px-4 border-b backdrop-blur-lg ${
            isDark
              ? 'bg-slate-900/95 border-white/10'
              : 'bg-white/95 border-slate-200 shadow-sm'
          }`}
        >
          {/* Burger + Logo */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className={`w-10 h-10 flex items-center justify-center rounded-xl transition-colors ${
                isDark
                  ? 'hover:bg-white/10 text-white'
                  : 'hover:bg-slate-100 text-slate-700'
              }`}
              aria-label="Ouvrir le menu"
            >
              <FontAwesomeIcon icon="bars" className="text-lg" />
            </button>
            <div className="flex items-center gap-2">
              <div className="relative w-8 h-8">
                <img src={appIcon} alt="Muzo Studio" className="w-full h-full object-contain" />
              </div>
              <span className={`font-bold text-lg tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Muzo<span className="text-indigo-500">Studio</span>
              </span>
            </div>
          </div>

          {/* Credits + Badge */}
          <div className="flex items-center gap-2">
            <div
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                isDark
                  ? 'bg-white/10 text-white'
                  : 'bg-slate-100 text-slate-700'
              }`}
            >
              <FontAwesomeIcon icon="coins" className="text-amber-400 text-xs" />
              <span>{credits ?? 0}</span>
            </div>
            {isMuzoPlus && (
              <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 text-[10px] font-bold px-2 py-1 shadow-sm">
                <FontAwesomeIcon icon="bolt" className="text-[9px]" />
                <span className="hidden xs:inline">Muzo+</span>
              </span>
            )}
          </div>
        </header>
      )}

      {/* Desktop Sidebar - Hidden on mobile */}
      <div className="hidden md:block">
        <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      </div>

      {/* Mobile Sidebar Drawer - Overlay style */}
      {isMobile && (
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 z-50 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
              isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Drawer */}
          <div
            className={`fixed top-0 left-0 z-50 h-full w-72 transform transition-transform duration-300 ease-out ${
              isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            <Sidebar
              isOpen={true}
              toggleSidebar={() => {}}
              isMobile={true}
              onCloseMobile={() => setIsMobileMenuOpen(false)}
            />
          </div>
        </>
      )}
      
      {/* Main content */}
      <main 
        className={`
          flex-1 
          transition-all 
          duration-300 
          ${isMobile ? 'ml-0 pt-14' : isSidebarOpen ? 'ml-56' : 'ml-14'}
          min-h-screen 
          overflow-x-hidden
        `}
      >
        <div className="h-full min-h-screen">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
